import {
    Intersection,
    Scene,
    Camera,
    Object3D,
    PerspectiveCamera,
    FileLoader,
    ObjectLoader,
    TextureLoader,
    WebGLRenderer,
    Mesh,
    MeshStandardMaterial,
    Face3,
    Vector2,
    Raycaster,
    Clock,
    Vector3,
} from "three";

import { FirstPersonControls } from "./controls/FirstPersonControls";
import { DeviceOrientationControls } from "three/examples/jsm/controls/DeviceOrientationControls";
import { VRGALLERY } from "../constants/constant";

export class ThreeHandler {
    // Intersection
    private intersected: Intersection | null = null;
    // Scene
    private scene: Scene = new Scene();
    // Camera
    private camera: PerspectiveCamera | null = null;
    // Renderer
    private renderer: WebGLRenderer | null = null;
    // Screen
    private screen: any = { w: 1600, h: 1000 };
    // Canvas
    private canvas: HTMLCanvasElement | null = null;
    // Camera Control
    private camControls: any | null = null;
    // Time Clock
    private clock: Clock = new Clock();
    // スマートフォンからアクセスされたか
    private isAccessedBySmartPhone: boolean = false;

    private preLocationOfCamera: Vector3 = new Vector3();
    // 衝突検知距離
    private static readonly COLLISION_DISTANCE: number = 2;

    constructor () { }

    public setIsAccessedBySmartPhone(isAccessedBySmartPhone: boolean = false) {
        this.isAccessedBySmartPhone = isAccessedBySmartPhone;
    }

    public initialize(canvas: HTMLCanvasElement) {
        this.canvas = canvas;
        this.screen.w = canvas.width;
        this.screen.h = canvas.height;
        this.createCamera();
        this.createRenderer();
    }

    public isUpdateLocation(isUpdate: boolean) {
        if (this.camControls != null) {
            this.camControls.enabled = isUpdate;
        }
    }

    /**
     * 画面サイズをリサイズした際の処理を実行します。
     *
     * @memberof ThreeHandler
     */
    public resize() {
        // Window Size
        var windowInnerWidth: number = window.parent.innerWidth;
        var windowInnerHeight: number = window.parent.innerHeight;

        if (this.canvas != null && this.renderer != null && this.camera != null) {
            // Reset Canvas Size
            this.canvas.setAttribute("width", windowInnerWidth.toString());
            this.canvas.setAttribute("height", windowInnerHeight.toString());
            // Renderer Reset
            this.renderer.setSize(this.canvas.width, this.canvas.height);
            // Camera Projection Reset
            this.camera.aspect = windowInnerWidth / windowInnerHeight;
            this.camera.updateProjectionMatrix();
        }
    }

    /**
     * Renderer を作成する.
     *
     * @private
     * @memberof ThreeHandler
     */
    private createRenderer() {
        if (this.canvas != null) {
            this.renderer = new WebGLRenderer({
                canvas: this.canvas,
                antialias: true,
                alpha: true
            });
            this.renderer.shadowMapEnabled = true;
            this.resize();
            this.renderer.setClearColor(0x000000, 0);
        }
    }

    /**
     * Camera を作成する。
     */
    private createCamera() {
        if (this.canvas != null) {
            // カメラ作成
            this.camera = new PerspectiveCamera(
                45,
                this.canvas.width / this.canvas.height,
                0.01,
                10000
            );
            // 初期位置を設定
            this.camera.position.set(10, 3, 5);
        }
    }

    private animate() {
        if (this.renderer != null && this.scene != null && this.camera != null) {
            requestAnimationFrame(() => this.renderer);
            this.renderer.render(this.scene, this.camera);
        }
    }

    /*
     * 視点制御（jsに元々含まれるソースを利用）
     */
    public createCameraControls(camera: Camera) {
        if (this.isAccessedBySmartPhone) {
            /*
            デバイス方向センサーのパーミッション設定。
            現状うまくいってないが、おそらく下記の記述を修正すれば行けそう？
            if (
                DeviceOrientationEvent &&
                window.DeviceOrientationEvent &&
                typeof DeviceMotionEvent.requestPermission === 'function'
            ) {
                DeviceMotionEvent.requestPermission();
            }*/
            // スマホ用
            // スマホデバイスに対応した View
            this.camControls = new DeviceOrientationControls(camera);
            this.camControls.connect();
        } else {
            // PC 用
            // 一人称視点View
            this.camControls = new FirstPersonControls(camera);
            // 設定
            this.camControls.lookSpeed = 0.05;
            this.camControls.movementSpeed = 10;
            this.camControls.lookVertical = true;
            this.camControls.constrainVertical = true;
            this.camControls.verticalMin = 1.0;
            this.camControls.verticalMax = 2.0;
            this.camControls.heightMin = 2.0;
            this.camControls.heightMax = 6.0;
            this.camControls.heightSpeed = true;
        }
    }

    /**
     * Sceneオブジェクトをレンダリングする。
     * @param afterFunction
     */
    public renderingScene(afterFunction: (element: Element) => void) {
        if (this.camera == null) {
            return;
        }
        // target object
        var targetLookAt = new Vector3(1, 4, 1);
        // camera look at
        this.camera.lookAt(targetLookAt);

        // camera controls setting
        this.createCameraControls(this.camera);

        //表示
        var animateFunc = () => {
            requestAnimationFrame(animateFunc);
            if (
                this.renderer != null &&
                this.scene != null &&
                this.camera != null &&
                this.camControls != null
            ) {
                let preLoc: Vector3 = new Vector3(
                    this.camera.position.x,
                    this.camera.position.y,
                    this.camera.position.z
                );
                // カメラ制御をアップデート
                this.camControls.update(this.clock.getDelta());

                // 衝突検知
                let isCollide: boolean = this.detectCollision(
                    preLoc,
                    this.camera.position
                );
                // 衝突検知した場合
                if (isCollide) {
                    this.camera.position.set(preLoc.x, preLoc.y, preLoc.z);
                }
                this.renderer.render(this.scene, this.camera);
            }
        };
        animateFunc();

        var canvas = document.querySelector("#three_canvas");
        if (canvas != null) {
            afterFunction(canvas);
        }
    }

    /**
     * SceneファイルをLoadする。
     */
    public loadScene(json_file_path: string | null, afterFunction: () => void, errorFunction: (params:any) => void): any {
        if (json_file_path == null) {
            console.log("Not Found 3D Json File.");
            return;
        }

        let result: XMLHttpRequest = new FileLoader().load(
            json_file_path,
            json_data => {
                //Scene 情報を格納している JSON をパースする
                var scenejson = JSON.parse(json_data.toString());
                //scene を作成する
                let sceneObj: Object3D = new ObjectLoader().parse(scenejson);
                this.scene = sceneObj as Scene;
                afterFunction();
            },
            /* on progress */
            (progress: ProgressEvent) => {
                // 進捗率を表示したいときはここを修正する。
                //console.log((progress.loaded / xhr.total * 100) + '% loaded');
            },
            /* on errors */
            (error: ErrorEvent) => {
                errorFunction(error);
            }
        );
    }

    /**
     * 指定されたCaptionの、タイトルと説明を取得する。
     * @param object
     */
    public getCaption(object: Object3D | null): {[key:string]:string} | null {
        if (object == null) return null;

        let objectName: string = object.name;
        // Object が Caption でない場合には null を return
        if (objectName.indexOf("Caption") !== 0) {
            return null;
        }

        // Caption の番号を取得
        let result: RegExpMatchArray | null = objectName.match(/\d+/g);

        if (result == null || result.length == 0) return null;
        let index: number = parseInt(result[0]) - 1;

        return {
            "title": VRGALLERY.CAPTIONS.TITLES[index],
            "description": VRGALLERY.CAPTIONS.DESCRIPTIONS[index]
        };
    }

    /**
     * 衝突を検知する。
     * Objectとの距離が接近しすぎているか否かで判定する。
     *
     * @param {Vector3} preLocation
     * @param {Vector3} currentLocation
     * @returns {boolean}
     * @memberof ThreeHandler
     */
    public detectCollision(preLocation: Vector3, currentLocation: Vector3): boolean {
        if (this.camera == null || this.scene == null) {
            return false;
        }

        // 方向ベクトル
        let direction: Vector3 = new Vector3(
            currentLocation.x - preLocation.x,
            currentLocation.y - preLocation.y,
            currentLocation.z - preLocation.z
        ).normalize();

        // 光線を作成
        var ray: Raycaster = new Raycaster(currentLocation, direction);

        // 衝突対象のObjectを設定
        let collisionObjects: Object3D[] = [];
        // とりあえずここでは壁のみに設定
        let wall: Object3D | undefined = this.scene.getObjectByName("Wall");
        if (typeof wall !== undefined && wall != null) {
            collisionObjects.push(wall);
        }

        // 距離算出
        var objs: Intersection[] = ray.intersectObjects(collisionObjects, true);
        if (objs.length > 0) {
            // 衝突検知（もっとも近いオブジェクトとの距離が一定内か）
            let obj: Intersection = objs[0];
            // Face Intersected
            let intersectedFace: Face3 | null | undefined = obj.face;

            let distanceFromPlane: number = 0;

            if (intersectedFace != null && typeof intersectedFace != undefined) {
                let normalized: Vector3 = intersectedFace.normal;
                // 内積
                let dotValue: number = Math.abs(normalized.dot(direction));
                // 対象物との垂直距離
                distanceFromPlane = dotValue * obj.distance;
            }
            if (distanceFromPlane < ThreeHandler.COLLISION_DISTANCE) {
                return true;
            }
        }

        return false;
    }

    /**
     * Object を 検知します。
     * 一番近くにある Object を一つだけ取得します。
     * @param {number} x
     * @param {number} y
     * @returns {(Intersection | null)}
     * @memberof ThreeHandler
     */
    public detectObject(x: number, y: number): Intersection | null {
        if (this.scene == null || this.camera == null || this.renderer == null) {
            return null;
        }
        // マウスクリック位置を正規化
        var mouse = new Vector2();
        var world = document.querySelector("canvas");
        if (world == null) {
            return null;
        }

        mouse.x = (x / world.clientWidth) * 2 - 1;
        mouse.y = -(y / world.clientHeight) * 2 + 1;

        // Raycasterインスタンス作成
        var raycaster = new Raycaster();
        // 取得したX、Y座標でrayの位置を更新
        raycaster.setFromCamera(mouse, this.camera);
        // オブジェクトの取得
        var intersects: Intersection[] = raycaster.intersectObjects(
            this.scene.children,
            true
        );

        // 検知しなかった場合
        if (intersects.length == 0) {
            return null;
        }

        let _mesh: Mesh | null = null;
        let _material: MeshStandardMaterial | null = null;

        return intersects.length > 0 ? intersects[0] : null;
    }

    /**
     * オブジェクトにテクスチャを貼り付ける。
     * @param objectType オブジェクトの種類（Floor:1, Wall:2）
     * @param filePath 画像のファイルパス
     * @param repeatX 画像のリピート数（x軸方向）
     * @param repeatY 画像のリピート数（y軸方向）
     * @param afterFunction 画像貼り付け後に実行する関数
     */
    public attachImageToObject(objectType: number, filePath: string, repeatX: number = 10, repeatY: number = 10, afterFunction: () => void) {
        if (!VRGALLERY.OBJECT.TYPES[objectType]) {
            console.log('');
            return;
        }
        var pictureTexture = new TextureLoader().load(filePath, data => {
            if (this.scene == null) {
                console.log('Not Found Scene Object.')
                return;
            }

            const OBJECT_TYPE_NAME: string = VRGALLERY.OBJECT.TYPES[objectType];

            var obj: Object3D | undefined = this.scene.getObjectByName(OBJECT_TYPE_NAME);
            if (typeof obj === "undefined" || obj == null) {
                console.log('Not Found ' + OBJECT_TYPE_NAME + ' Object.');
                return;
            }

            let material: MeshStandardMaterial = (obj as Mesh)
                .material as MeshStandardMaterial;
            material.side = 1;
            data.repeat.set(repeatX, repeatY);
            data.wrapT = 1000;
            data.wrapS = 1000;
            data.needsUpdate = true;
            material.map = data;
            material.bumpMap = data;
            material.bumpScale = VRGALLERY.OBJECT.BUMP_SCALES[objectType];
            material.needsUpdate = true;

            afterFunction();
        });
    }
}
